import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  LinearProgress,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Utility: Format duration in human-readable format
const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return 'N/A';
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
};

// Utility: Get color based on SLA compliance percentage
const getSLAColor = (percentage) => {
  if (percentage >= 95) return 'success.main';
  if (percentage >= 80) return 'warning.main';
  return 'error.main';
};

/**
 * MTTR Widget - Mean Time To Resolution
 * Displays the average time to resolve alerts
 */
export const MTTRWidget = ({
  value,
  subtitle = 'Last 7 days average',
  loading = false,
  sx = {},
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <TimerIcon sx={{ color: 'info.main', mr: 1 }} />
        <Typography variant="subtitle2">MTTR</Typography>
        <Tooltip title="Mean Time To Resolution - Average time to resolve alerts">
          <IconButton size="small" sx={{ ml: 0.5 }}>
            <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
      </Box>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {formatDuration(value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </>
      )}
    </Paper>
  );
};

/**
 * MTTA Widget - Mean Time To Acknowledge
 * Displays the average time to acknowledge alerts
 */
export const MTTAWidget = ({
  value,
  subtitle = 'Last 7 days average',
  loading = false,
  sx = {},
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AccessTimeIcon sx={{ color: 'warning.main', mr: 1 }} />
        <Typography variant="subtitle2">MTTA</Typography>
        <Tooltip title="Mean Time To Acknowledge - Average time to acknowledge alerts">
          <IconButton size="small" sx={{ ml: 0.5 }}>
            <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
      </Box>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {formatDuration(value)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </>
      )}
    </Paper>
  );
};

/**
 * SLA Compliance Widget
 * Displays the SLA compliance percentage with visual progress bar
 */
export const SLAComplianceWidget = ({
  value = 0,
  slaTarget = 30,
  subtitle,
  loading = false,
  sx = {},
}) => {
  const displayValue = value || 0;
  const defaultSubtitle = `Alerts acknowledged within ${slaTarget} min SLA`;

  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <SpeedIcon sx={{ color: 'primary.main', mr: 1 }} />
        <Typography variant="subtitle2">SLA Compliance</Typography>
      </Box>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: getSLAColor(displayValue)
            }}
          >
            {displayValue}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={displayValue}
            sx={{
              mt: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: getSLAColor(displayValue)
              }
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {subtitle || defaultSubtitle}
          </Typography>
        </>
      )}
    </Paper>
  );
};

/**
 * Alerts by Severity Widget
 * Displays alert count breakdown by severity level
 */
export const AlertsBySeverityWidget = ({
  critical = 0,
  high = 0,
  warning = 0,
  info = 0,
  loading = false,
  sx = {},
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box', ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Alerts by Severity
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip label="Critical" size="small" color="error" />
            <Typography variant="body2" fontWeight={600}>
              {critical}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip label="High" size="small" color="error" variant="outlined" />
            <Typography variant="body2" fontWeight={600}>
              {high}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip label="Warning" size="small" color="warning" />
            <Typography variant="body2" fontWeight={600}>
              {warning}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip label="Info" size="small" color="info" />
            <Typography variant="body2" fontWeight={600}>
              {info}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

/**
 * Alerts by Source Widget
 * Displays alert count breakdown by source (Logs, Metrics, Traces)
 */
export const AlertsBySourceWidget = ({
  logs = 0,
  metrics = 0,
  traces = 0,
  loading = false,
  sx = {},
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box', ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Alerts by Source
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="LOGS" size="small" color="primary" variant="outlined" />
              <Typography variant="body2">OpenSearch</Typography>
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {logs}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="METRICS" size="small" color="secondary" variant="outlined" />
              <Typography variant="body2">VictoriaMetrics</Typography>
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {metrics}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label="TRACES" size="small" color="info" variant="outlined" />
              <Typography variant="body2">Tempo</Typography>
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {traces}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

/**
 * Top Alerting Rules Widget
 * Displays the rules that generate the most alerts
 */
export const TopAlertingRulesWidget = ({
  rules = [],
  maxRules = 5,
  loading = false,
  sx = {},
}) => {
  return (
    <Paper sx={{ p: 2, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box', ...sx }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Top Alerting Rules (Last 7 Days)
      </Typography>
      {loading ? (
        <CircularProgress size={24} />
      ) : rules.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rules.slice(0, maxRules).map((rule, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  #{idx + 1}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {rule.name}
                </Typography>
              </Box>
              <Chip label={`${rule.alertCount} alerts`} size="small" variant="outlined" />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No alert data available
        </Typography>
      )}
    </Paper>
  );
};

// Default export with all widgets
export default {
  MTTRWidget,
  MTTAWidget,
  SLAComplianceWidget,
  AlertsBySeverityWidget,
  AlertsBySourceWidget,
  TopAlertingRulesWidget,
};
